import { Check, Sparkles } from "lucide-react";
import { Link } from "react-router-dom";
import { useLocalePath, useTranslation } from "@thyra/landing-i18n";
import { TooltipCard } from "./ui/TooltipCard";
import { SectionHeader } from "./ui/SectionHeader";
import { getAppUrl } from "../env";

const tiers = [
  {
    id: "starter",
    periodKey: "pricing.plans.starter.period",
    badgeKey: null,
    features: [
      "pricing.plans.starter.features.assistants",
      "pricing.plans.starter.features.setups",
      "pricing.plans.starter.features.integrations",
      "pricing.plans.starter.features.voice",
      "pricing.plans.starter.features.usage",
    ],
    ctaExternal: true,
    ctaTo: null,
    highlighted: false,
  },
  {
    id: "pro",
    periodKey: "pricing.plans.pro.period",
    badgeKey: "pricing.plans.pro.badge",
    features: [
      "pricing.plans.pro.features.assistants",
      "pricing.plans.pro.features.setups",
      "pricing.plans.pro.features.integrations",
      "pricing.plans.pro.features.newFeatures",
      "pricing.plans.pro.features.usage",
    ],
    ctaExternal: true,
    ctaTo: null,
    highlighted: true,
  },
  {
    id: "enterprise",
    periodKey: null,
    badgeKey: null,
    features: [
      "pricing.plans.enterprise.features.assistants",
      "pricing.plans.enterprise.features.controls",
      "pricing.plans.enterprise.features.support",
    ],
    ctaExternal: false,
    ctaTo: "/contact",
    highlighted: false,
  },
] as const;

export function PricingSection() {
  const { t } = useTranslation("sections");
  const lp = useLocalePath();
  return (
    <section id="pricing" className="py-20 sm:py-28 bg-background-muted">
      <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
        <SectionHeader
          eyebrow={t("pricing.eyebrow")}
          title={t("pricing.title")}
          description={t("pricing.description")}
        />
        <div className="mt-16 grid grid-cols-1 gap-6 lg:grid-cols-3 lg:gap-8">
          {tiers.map((tier, i) => (
            <div
              key={tier.id}
              data-aos="fade-up"
              data-aos-delay={i * 90}
              className={`marketing-card marketing-card-hover relative p-6 ${
                tier.highlighted ? "lg:-my-2 lg:py-8" : ""
              }`}
              style={
                tier.highlighted
                  ? { boxShadow: "var(--shadow-glow-primary)" }
                  : undefined
              }
            >
              {tier.badgeKey ? (
                <span
                  className="absolute -top-3 left-1/2 inline-flex -translate-x-1/2 items-center gap-1 rounded-full px-3 py-1 text-xs font-semibold text-white shadow-sm shadow-primary/30"
                  style={{ background: "var(--gradient-brand)" }}
                >
                  <Sparkles className="h-3 w-3" aria-hidden />
                  {t(tier.badgeKey)}
                </span>
              ) : null}
              {tier.highlighted ? (
                <TooltipCard
                  content={
                    <p className="text-muted-foreground">
                      {t("pricing.plans.pro.tooltip")}
                    </p>
                  }
                >
                  <h3 className="text-lg font-semibold text-card-foreground">
                    {t(`pricing.plans.${tier.id}.name`)}
                  </h3>
                </TooltipCard>
              ) : (
                <h3 className="text-lg font-semibold text-card-foreground">
                  {t(`pricing.plans.${tier.id}.name`)}
                </h3>
              )}
              <div className="mt-4 flex items-baseline gap-1">
                <span className="text-3xl font-bold text-card-foreground">
                  {t(`pricing.plans.${tier.id}.price`)}
                </span>
                {tier.periodKey ? (
                  <span className="text-muted-foreground">
                    {t(tier.periodKey)}
                  </span>
                ) : null}
              </div>
              <p className="mt-2 text-sm text-muted-foreground">
                {t(`pricing.plans.${tier.id}.description`)}
              </p>
              <ul className="mt-6 space-y-3">
                {tier.features.map((feature) => (
                  <li
                    key={feature}
                    className="flex items-center gap-2 text-sm text-muted-foreground"
                  >
                    <Check className="h-5 w-5 shrink-0 text-primary" />
                    {t(feature)}
                  </li>
                ))}
              </ul>
              {tier.ctaExternal ? (
                <a
                  href={getAppUrl()}
                  className={`mt-8 inline-flex w-full items-center justify-center rounded-xl px-4 py-3 text-sm font-semibold transition-colors ${
                    tier.highlighted
                      ? "btn-brand"
                      : "border border-border bg-background text-foreground hover:border-primary/30 hover:bg-primary/5 hover:text-primary"
                  }`}
                >
                  {t(`pricing.plans.${tier.id}.cta`)}
                </a>
              ) : tier.ctaTo ? (
                <Link
                  to={lp(tier.ctaTo)}
                  className="mt-8 inline-flex w-full items-center justify-center rounded-lg border border-border bg-background px-4 py-3 text-sm font-medium text-foreground transition-colors hover:bg-muted"
                >
                  {t(`pricing.plans.${tier.id}.cta`)}
                </Link>
              ) : null}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
